import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function TriviaVancouverCanvas() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-trivia-guide";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles
    ? articles.find((art) => art.slug === ref_id)
    : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Best Pub Trivia Nights",
    "section-2": "Unique Trivia Experiences",
    "section-3": "Best Online Trivia Communities",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.hellobc.com",
      label: "Destination BC – Nightlife and Pubs",
    },
    {
      url: "https://www2.gov.bc.ca",
      label: "BC Government – Legal Drinking Age & ID Requirements",
    },
    {
      url: "https://www.iq2000trivia.com",
      label: "IQ 2000 Trivia",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" must match the ref used in the text.
  // Coordinates are included with 5 decimals (approximate).
  const locations = [
    // --- Best Pub Trivia Nights ---
    {
      id: "mooses-down-under",
      lat: 49.28533,
      lng: -123.11790,
      popupText: "Moose’s Down Under (Downtown)",
    },
    {
      id: "the-american",
      lat: 49.27756,
      lng: -123.09930,
      popupText: "The American (Mount Pleasant/Chinatown)",
    },
    {
      id: "heros-welcome",
      lat: 49.25150,
      lng: -123.10098,
      popupText: "Hero’s Welcome (Main Street – Riley Park)",
    },
    {
      id: "lamplighter-public-house",
      lat: 49.28365,
      lng: -123.10645,
      popupText: "The Lamplighter Public House (Gastown)",
    },
    {
      id: "east-side-craft-house",
      lat: 49.23236,
      lng: -123.07621,
      popupText: "East Side Craft House (East Vancouver)",
    },
    {
      id: "relish-pub",
      lat: 49.28005,
      lng: -123.12531,
      popupText: "Relish The Pub (Downtown)",
    },
    {
      id: "darbys-pub",
      lat: 49.26854,
      lng: -123.16810,
      popupText: "Darby’s Pub (Kitsilano)",
    },
    {
      id: "wolf-and-hound",
      lat: 49.26492,
      lng: -123.18545,
      popupText: "The Wolf & Hound (Point Grey/Kitsilano)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>
          {ref_data?.title || "Vancouver Trivia Guide – Best Quiz Nights & More"}
        </title>
        <meta
          name="description"
          content={
            ref_data?.excerpt ||
            "Discover the best trivia nights in Vancouver, unique quiz events, online trivia communities, and practical tips."
          }
        />
        <meta
          name="keywords"
          content="Vancouver trivia, quiz nights, pub trivia, IQ 2000 Trivia"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-trivia-guide.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content={
            ref_data?.title || "Vancouver Trivia Guide – Best Quiz Nights & More"
          }
        />
        <meta
          property="og:description"
          content={
            ref_data?.excerpt ||
            "Discover the best trivia nights in Vancouver, unique quiz events, online trivia communities, and practical tips."
          }
        />
        <meta
          property="og:image"
          content={
            "https://bcouver.com" +
            (ref_data?.image || "/assets/images/vancouver-trivia-guide.jpg")
          }
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-trivia-guide.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            ref_data?.title || "Vancouver Trivia Guide – Best Quiz Nights & More"
          }
        />
        <meta
          name="twitter:description"
          content={
            ref_data?.excerpt ||
            "Discover the best trivia nights in Vancouver, unique quiz events, online trivia communities, and practical tips."
          }
        />
        <meta
          name="twitter:image"
          content={
            "https://bcouver.com" +
            (ref_data?.image || "/assets/images/vancouver-trivia-guide.jpg")
          }
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/vancouver-trivia-guide.jpg"
              }
              alt={ref_data?.["image-desc"] || "Best Trivia in Vancouver"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s trivia scene is booming, from classic pub quizzes to
              themed trivia extravaganzas and vibrant online communities. Whether
              you’re a seasoned quizmaster or just looking for a fun night out,
              there’s something for everyone. This guide will help you discover
              the best bars, unique trivia events, and online groups, so you can
              flex your knowledge and have a blast in beautiful Vancouver.
            </p>

            {/* SECTION 1: Best Pub Trivia Nights */}
            <h2 id="section-1">Best Pub Trivia Nights</h2>
            <p>
              Looking for a lively bar or pub to test your knowledge? Vancouver
              offers trivia nights nearly every night of the week. Below are some
              top venues with addresses, times, and approximate costs. Bring your
              friends, form a team, and compete for prizes or the glory of winning!
            </p>

            {/* Moose’s Down Under */}
            <p
              ref={(el) => setSpotRef("mooses-down-under", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Moose’s Down Under (Downtown)</strong>{" "}
              <Marker
                coordY={49.28533}
                coordX={-123.11790}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – Basement, 830 W Pender St, Vancouver, BC V6C 1J8. Trivia on
              Wednesdays at 7:00&nbsp;PM, free to play. This Aussie-themed bar
              gets busy, so reservations are recommended!
            </p>

            {/* The American */}
            <p
              ref={(el) => setSpotRef("the-american", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The American (Mount Pleasant/Chinatown)</strong>{" "}
              <Marker
                coordY={49.27756}
                coordX={-123.09930}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 926 Main St, Vancouver, BC V6A 2W1. Trivia Tuesdays at 7:00&nbsp;PM
              hosted by IQ 2000. Spacious bar with arcade games and pool tables.
              Arrive early to secure a table.
            </p>

            {/* Hero’s Welcome */}
            <p
              ref={(el) => setSpotRef("heros-welcome", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hero’s Welcome (Main Street – Riley Park)</strong>{" "}
              <Marker
                coordY={49.25150}
                coordX={-123.10098}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 3917 Main St, Vancouver, BC V5V 3P3. Trivia on Tuesdays at
              7:00&nbsp;PM. Free entry, fun community pub atmosphere, and minors
              are allowed until 8:00&nbsp;PM with a guardian.
            </p>

            {/* Lamplighter Public House */}
            <p
              ref={(el) => setSpotRef("lamplighter-public-house", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Lamplighter Public House (Gastown)</strong>{" "}
              <Marker
                coordY={49.28365}
                coordX={-123.10645}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 92 Water St, Vancouver, BC V6B 1A4. Trivia Tuesdays at 7:00&nbsp;PM.
              Historic venue, big crowds, arrive early or risk standing!
            </p>

            {/* East Side Craft House */}
            <p
              ref={(el) => setSpotRef("east-side-craft-house", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>East Side Craft House (East Vancouver)</strong>{" "}
              <Marker
                coordY={49.23236}
                coordX={-123.07621}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 1445 E 41st Ave, Vancouver, BC V5P 1J8. Trivia on Saturdays at
              7:00&nbsp;PM. Neighborhood sports pub with local craft beers; laid-back
              vibe for a weekend quiz night.
            </p>

            {/* Relish The Pub */}
            <p
              ref={(el) => setSpotRef("relish-pub", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Relish The Pub (Downtown)</strong>{" "}
              <Marker
                coordY={49.28005}
                coordX={-123.12531}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 888 Nelson St, Vancouver, BC V6Z 2S1. Trivia every other Tuesday at
              7:00&nbsp;PM. Check schedules for exact dates; often prizes include gift
              cards or free drinks.
            </p>

            {/* Darby’s Pub */}
            <p
              ref={(el) => setSpotRef("darbys-pub", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Darby’s Pub (Kitsilano)</strong>{" "}
              <Marker
                coordY={49.26854}
                coordX={-123.16810}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 2001 Macdonald St, Vancouver, BC V6K 3Y2. Trivia Tuesdays at
              7:30&nbsp;PM. Popular with the Kits/UBC crowd; expect a busy scene,
              rooftop patio in summer.
            </p>

            {/* The Wolf & Hound */}
            <p
              ref={(el) => setSpotRef("wolf-and-hound", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Wolf & Hound (Point Grey/Kitsilano)</strong>{" "}
              <Marker
                coordY={49.26492}
                coordX={-123.18545}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – 3617 W Broadway, Vancouver, BC V6R 2B8. Trivia Thursdays at
              8:00&nbsp;PM. Cozy Irish pub; known for challenging quiz questions
              and a loyal crowd.
            </p>

            <p>
              <em>Pro Tip:</em> Most pub trivia is free, but plan to buy food or
              drinks to support the venue. Popular spots fill up quickly, so arrive
              early or reserve a table if possible.
            </p>

            {/* SECTION 2: Unique Trivia Experiences */}
            <h2 id="section-2">Unique Trivia Experiences</h2>
            <p>
              Not a fan of the typical bar quiz? Vancouver also has special themed
              events, trivia fundraisers, and more:
            </p>
            <ul>
              <li>
                <strong>Themed Trivia Nights:</strong> Harry Potter, Marvel,
                Disney, and more – often hosted at bars like The American or Malone’s
                Taphouse. These events can be ticketed or first-come, so check
                social media for details.
              </li>
              <li>
                <strong>Trivia Fundraisers:</strong> Some trivia nights double as
                charity or community events, held in community centers or large
                halls. Keep an eye on local event listings for one-off nights.
              </li>
              <li>
                <strong>Interactive Variations:</strong> Try music bingo, name-that-tune
                contests, or quiz bowls run by university clubs. They add a unique
                twist to the classic trivia format.
              </li>
            </ul>
            <p>
              Keep an eye on event platforms (like Eventbrite) or your favorite
              trivia hosts’ social media to catch these special-themed nights and
              offbeat quiz experiences.
            </p>

            {/* SECTION 3: Best Online Trivia Communities */}
            <h2 id="section-3">Best Online Trivia Communities</h2>
            <p>
              Prefer to quiz from the comfort of home or looking to connect with other
              trivia enthusiasts? Here’s where you can find Vancouver’s online
              trivia community:
            </p>
            <ul>
              <li>
                <strong>IQ 2000 Trivia & Vancouver Trivia Party:</strong> Follow
                them on Instagram or Facebook for schedule updates, themed events,
                and last-minute venue changes. They also share photos of winning
                teams and bonus questions.
              </li>
              <li>
                <strong>Reddit r/vancouver:</strong> Locals frequently discuss their
                favorite trivia nights. It’s a great way to discover new venues,
                find teammates, or read reviews.
              </li>
              <li>
                <strong>Virtual Trivia Meetups:</strong> Groups on Meetup.com or
                Facebook often host Zoom-based trivia sessions, perfect for rainy
                nights or if you’re stuck at home.
              </li>
            </ul>
            <p>
              Online communities are an excellent way to find trivia partners,
              practice with sample questions, or learn about upcoming themed events.
              Some people even transition from online teams to in-person bar teams,
              forging new friendships and friendly rivalries along the way!
            </p>

            {/* FAQ SECTION */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need to book ahead for trivia nights?</h3>
              <p>
                Many popular venues fill up quickly, so reservations or early arrival
                is recommended. Some places, like Moose’s Down Under, strongly
                encourage calling ahead.
              </p>

              <h3>2. Are trivia nights always free?</h3>
              <p>
                Yes, most Vancouver trivia nights are free to play, but you’re
                expected to purchase food or drinks to support the host venue.
                Special themed events may occasionally have a cover charge or
                donation requirement.
              </p>

              <h3>3. What is the legal drinking age in BC?</h3>
              <p>
                The legal drinking age is 19. However, some “food primary” pubs allow
                minors until a set time if accompanied by an adult. Always check the
                venue’s age policy beforehand.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              In British Columbia, liquor-licensed venues typically require patrons to
              be 19 or older. Some pubs permit under-19s in designated areas until a
              certain hour if they have a “food primary” license. Always have valid ID
              ready. For any special event regulations (like capacity or health
              guidelines), check official sources or contact the venue.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From beloved pubs with weekly quizzes to niche themed trivia nights and
              online meetups, Vancouver offers plenty of opportunities to show off
              your knowledge. Grab your friends (or join a community online), pick a
              spot from this guide, and prepare for a fun, mind-stretching evening.
              Whether you win or lose, you’ll undoubtedly learn something new—and
              that’s what trivia is all about. Good luck and happy quizzing!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}